import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch } from '../../api';

const { FiX, FiCheck, FiChevronRight, FiChevronLeft, FiPlus, FiTrash2, FiLoader, FiAlertCircle } = FiIcons;

const CATEGORIES = ['Phishing Education', 'Authentication', 'Physical Security', 'Compliance Training', 'Policy Acknowledgement'];
const FORMATS = ['Video', 'Interactive', 'Course', 'Micro-learning', 'Quiz'];
const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];
const COMPLIANCE_TAGS = ['NIST CSF', 'ISO 27001', 'SOC 2', 'GDPR', 'HIPAA', 'CCPA', 'PCI DSS'];

const defaultForm = {
  title: '', category: CATEGORIES[0], deliveryFormat: FORMATS[0],
  difficultyLevel: DIFFICULTIES[0], duration: '', description: '',
  linkedComplianceTopic: '', complianceTags: [], status: 'Draft',
  video_url: '', embedVideo: ''
};

const getEmbedUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/);
  if (ytMatch && ytMatch[1]) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch && vimeoMatch[1]) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch && driveMatch[1]) return `https://drive.google.com/file/d/${driveMatch[1]}/preview`;
  if (url.includes('app.dadan.io')) return url;
  if (url.includes('embed') || url.endsWith('.mp4')) return url;
  return null;
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-slate-50 focus:bg-white";

const CreateModuleModal = ({ onClose, onSave, initialData }) => {
  const isEditing = !!initialData;
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const [form, setForm] = useState(initialData || defaultForm);
  const [questions, setQuestions] = useState([]);
  const [fetchedQuestions, setFetchedQuestions] = useState(false);
  
  const [selectedCampaigns, setSelectedCampaigns] = useState(
    initialData ? (typeof initialData.assignedCampaigns === 'string' ? JSON.parse(initialData.assignedCampaigns) : (initialData.assignedCampaigns || [])) : []
  );

  const [embedInput, setEmbedInput] = useState(initialData?.embedVideo || '');
  const [embedError, setEmbedError] = useState('');
  const [safeEmbed, setSafeEmbed] = useState(initialData?.embedVideo || '');

  useEffect(() => {
    if (step === 3 && campaigns.length === 0) {
      setLoadingCampaigns(true);
      dataFetch('read/campaigns').then(res => {
        setCampaigns(res?.data || []);
        setLoadingCampaigns(false);
      });
    }
  }, [step]);

  // Load associated quiz questions if editing
  useEffect(() => {
    if (isEditing && initialData?.id && !fetchedQuestions) {
      setFetchedQuestions(true);
      dataFetch(`read/quiz_questions?module_id=${initialData.id}`).then(res => {
        if (res?.status === 'success' && res.data) {
          const parsed = res.data.map(q => ({
            id: q.id,
            question: q.question || '',
            options: typeof q.options === 'string' ? JSON.parse(q.options) : (q.options || ['', '', '', '']),
            explanations: typeof q.explanations === 'string' ? JSON.parse(q.explanations) : (q.explanations || ['', '', '', '']),
            answer: q.answer || ''
          }));
          setQuestions(parsed);
        }
      });
    }
  }, [isEditing, initialData, fetchedQuestions]);

  const upd = patch => setForm(f => ({ ...f, ...patch }));
  const toggleTag = tag => upd({ complianceTags: form.complianceTags.includes(tag) ? form.complianceTags.filter(t => t !== tag) : [...form.complianceTags, tag] });
  const toggleCampaign = id => setSelectedCampaigns(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const canProceedStep1 = form.title.trim() && form.duration.trim() && form.description.trim() && (form.deliveryFormat !== 'Video' || form.embedVideo || form.video_url?.trim());

  const handleAddQuestion = () => setQuestions([...questions, { question: '', options: ['', '', '', ''], explanations: ['', '', '', ''], answer: '' }]);
  const handleRemoveQuestion = index => setQuestions(questions.filter((_, i) => i !== index));
  const updQuestion = (index, patch) => {
    const next = [...questions];
    next[index] = { ...next[index], ...patch };
    setQuestions(next);
  };
  const updOption = (qIndex, oIndex, val) => {
    const next = [...questions];
    next[qIndex].options[oIndex] = val;
    setQuestions(next);
  };
  const updExplanation = (qIndex, oIndex, val) => {
    const next = [...questions];
    if (!next[qIndex].explanations) next[qIndex].explanations = ['', '', '', ''];
    next[qIndex].explanations[oIndex] = val;
    setQuestions(next);
  };

  const handleEmbedChange = (val) => {
    setEmbedInput(val);
    if (!val.trim()) {
      setSafeEmbed('');
      setEmbedError('');
      upd({ embedVideo: '' });
      return;
    }
    
    try {
      const parser = new DOMParser();
      const doc = parser.parseDocument(val, 'text/html');
      const iframe = doc.querySelector('iframe');
      
      if (!iframe) {
        setEmbedError('No iframe found in the embed code.');
        setSafeEmbed('');
        return;
      }
      
      let src = iframe.getAttribute('src');
      if (!src) {
        setEmbedError('Iframe is missing a src attribute.');
        setSafeEmbed('');
        return;
      }
      
      if (src.startsWith('//')) {
        src = 'https:' + src;
      }
      
      const url = new URL(src);
      const trustedDomains = ['app.dadan.io', 'www.youtube.com', 'youtube.com', 'player.vimeo.com', 'www.loom.com'];
      
      if (!trustedDomains.some(d => url.hostname === d || url.hostname.endsWith('.' + d))) {
        setEmbedError(`Untrusted domain: ${url.hostname}. Supported: Dadan, YouTube, Vimeo, Loom.`);
        setSafeEmbed('');
        return;
      }
      
      const cleanHtml = `<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 0.5rem; background: #000;">
        <iframe src="${src}" style="position: absolute; border: 0; width: 100%; height: 100%; inset: 0;" allowfullscreen webkitallowfullscreen mozallowfullscreen loading="lazy" allow="fullscreen; autoplay; picture-in-picture"></iframe>
      </div>`;
      
      setEmbedError('');
      setSafeEmbed(cleanHtml);
      upd({ embedVideo: cleanHtml });
    } catch (e) {
      setEmbedError('Invalid embed code format.');
      setSafeEmbed('');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const userId = localStorage.getItem('user_id');

    const modulePayload = {
      title: form.title,
      category: form.category,
      deliveryFormat: form.deliveryFormat,
      difficultyLevel: form.difficultyLevel,
      duration: form.duration,
      description: form.description,
      linkedComplianceTopic: form.linkedComplianceTopic,
      complianceTags: JSON.stringify(form.complianceTags),
      status: form.status,
      embedVideo: form.embedVideo || null,
      video_url: form.deliveryFormat === 'Video' ? form.video_url : null,
      user_id: userId
    };

    if (isEditing) {
      modulePayload.usageCount = selectedCampaigns.length;
      modulePayload.assignedCampaigns = JSON.stringify(selectedCampaigns);
      if (selectedCampaigns.length > 0 && !form.lastUsed) {
        modulePayload.lastUsed = new Date().toISOString().split('T')[0];
      } else {
        modulePayload.lastUsed = form.lastUsed;
      }

      const modRes = await dataFetch(`update/modules/${initialData.id}`, {
        method: 'PUT',
        body: JSON.stringify(modulePayload)
      });

      if (modRes?.status === 'success') {
        const validQuestions = questions.filter(q => q.question.trim() && q.answer.trim());
        
        try {
          const origQRes = await dataFetch(`read/quiz_questions?module_id=${initialData.id}`);
          const origQs = origQRes?.data || [];
          
          const validIds = validQuestions.map(q => q.id).filter(Boolean);
          const toDelete = origQs.filter(oq => !validIds.includes(oq.id));
          
          if (toDelete.length > 0) {
            await dataFetch('bulk/delete/quiz_questions', {
              method: 'DELETE',
              body: JSON.stringify({ ids: toDelete.map(q => q.id) })
            });
          }
          
          const toUpdate = validQuestions.filter(q => q.id);
          if (toUpdate.length > 0) {
            const updateRecords = toUpdate.map(q => ({
              id: q.id,
              module_id: String(initialData.id),
              question: q.question,
              options: JSON.stringify(q.options),
              explanations: JSON.stringify(q.explanations || ['', '', '', '']),
              answer: q.answer,
              user_id: userId
            }));
            await dataFetch('bulk/update/quiz_questions', {
              method: 'PUT',
              body: JSON.stringify({ records: updateRecords })
            });
          }
          
          const toCreate = validQuestions.filter(q => !q.id);
          if (toCreate.length > 0) {
            const createRecords = toCreate.map(q => ({
              module_id: String(initialData.id),
              question: q.question,
              options: JSON.stringify(q.options),
              explanations: JSON.stringify(q.explanations || ['', '', '', '']),
              answer: q.answer,
              user_id: userId
            }));
            await dataFetch('bulk/create/quiz_questions', {
              method: 'POST',
              body: JSON.stringify({ records: createRecords })
            });
          }
        } catch (err) {
          console.error('Failed to sync quiz questions', err);
        }

        onSave({ 
          ...initialData, 
          ...modulePayload, 
          complianceTags: form.complianceTags,
          assignedCampaigns: selectedCampaigns 
        });
      } else {
        alert('Failed to update module');
      }
      setLoading(false);
      return;
    }

    // Creation Logic
    modulePayload.usageCount = selectedCampaigns.length;
    modulePayload.lastUsed = selectedCampaigns.length > 0 ? new Date().toISOString().split('T')[0] : null;
    modulePayload.assignedCampaigns = JSON.stringify(selectedCampaigns);
    modulePayload.thumbnail = 'compliance';
    modulePayload.language = 'en';

    const modRes = await dataFetch('create/modules', {
      method: 'POST',
      body: JSON.stringify(modulePayload)
    });

    if (modRes?.status === 'success' && modRes?.data?.id) {
      const newModuleId = String(modRes.data.id);

      const validQuestions = questions.filter(q => q.question.trim() && q.answer.trim());
      if (validQuestions.length > 0) {
        const qRecords = validQuestions.map(q => ({
          module_id: newModuleId,
          question: q.question,
          options: JSON.stringify(q.options),
          explanations: JSON.stringify(q.explanations || ['', '', '', '']),
          answer: q.answer,
          user_id: userId
        }));
        await dataFetch('bulk/create/quiz_questions', {
          method: 'POST',
          body: JSON.stringify({ records: qRecords })
        });
      }

      onSave({
        ...modulePayload,
        id: modRes.data.id,
        complianceTags: form.complianceTags,
        assignedCampaigns: selectedCampaigns,
        createdAt: new Date().toISOString().split('T')[0]
      });
    } else {
      alert('Failed to create module');
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
        <motion.div initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col z-10">

          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <div>
              <h2 className="text-base font-semibold text-slate-800">{isEditing ? 'Edit Training Module' : 'Create Training Module'}</h2>
              <p className="text-xs text-slate-500 mt-0.5">Step {step} of 3</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100">
              <SafeIcon icon={FiX} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
            {step === 1 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <Field label="Module Title" required>
                  <input value={form.title} onChange={e => upd({ title: e.target.value })} placeholder="e.g. Phishing Awareness Fundamentals" className={inputCls} />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Category" required>
                    <select value={form.category} onChange={e => upd({ category: e.target.value })} className={inputCls}>
                      {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Delivery Format" required>
                    <select value={form.deliveryFormat} onChange={e => upd({ deliveryFormat: e.target.value })} className={inputCls}>
                      {FORMATS.map(f => <option key={f}>{f}</option>)}
                    </select>
                  </Field>
                </div>

                {form.deliveryFormat === 'Video' && !form.embedVideo && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-3">
                    <Field label="Video Link">
                      <input 
                        value={form.video_url || ''} 
                        onChange={e => upd({ video_url: e.target.value })} 
                        placeholder="e.g. https://youtube.com/watch?v=... or Vimeo link" 
                        className={inputCls} 
                      />
                    </Field>
                    {form.video_url && getEmbedUrl(form.video_url) ? (
                      <div className="w-full aspect-video bg-black rounded-lg overflow-hidden border border-slate-200">
                        <iframe
                          src={getEmbedUrl(form.video_url)}
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="Video Preview"
                        />
                      </div>
                    ) : form.video_url ? (
                      <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                        Preview unavailable. Make sure the link is a valid Dadan, YouTube, Vimeo, or Google Drive URL.
                      </p>
                    ) : null}
                  </motion.div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <Field label="Difficulty Level">
                    <div className="flex gap-2">
                      {DIFFICULTIES.map(d => (
                        <button key={d} type="button" onClick={() => upd({ difficultyLevel: d })}
                          className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${form.difficultyLevel === d ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                          {d}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Duration" required>
                    <input value={form.duration} onChange={e => upd({ duration: e.target.value })} placeholder="e.g. 15 min" className={inputCls} />
                  </Field>
                </div>

                <Field label="Description" required>
                  <textarea value={form.description} onChange={e => upd({ description: e.target.value })} rows={3}
                    placeholder="Describe what learners will gain from this module…" className={`${inputCls} resize-none`} />
                </Field>

                <Field label="Linked Compliance Topic">
                  <input value={form.linkedComplianceTopic} onChange={e => upd({ linkedComplianceTopic: e.target.value })}
                    placeholder="e.g. NIST CSF - PR.AT" className={inputCls} />
                </Field>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Compliance Tags</p>
                  <div className="flex flex-wrap gap-2">
                    {COMPLIANCE_TAGS.map(tag => (
                      <button key={tag} type="button" onClick={() => toggleTag(tag)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${form.complianceTags.includes(tag) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Publish Status</p>
                  <div className="flex gap-2">
                    {['Draft', 'Active'].map(s => (
                      <button key={s} type="button" onClick={() => upd({ status: s })}
                        className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-colors ${form.status === s ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Embedded Video (Optional)</h3>
                    {safeEmbed && (
                      <button type="button" onClick={() => handleEmbedChange('')} className="text-xs font-semibold text-red-500 hover:text-red-600 transition-colors">
                        Clear Embed
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-3">Paste iframe code from trusted providers (Dadan, YouTube, Vimeo, Loom).</p>
                  
                  <textarea 
                    value={embedInput} 
                    onChange={e => handleEmbedChange(e.target.value)} 
                    rows={4}
                    placeholder={`<iframe src="https://app.dadan.io/..." ...></iframe>`} 
                    className={`${inputCls} font-mono text-xs`} 
                  />
                  
                  {embedError && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                      <SafeIcon icon={FiAlertCircle} /> {embedError}
                    </p>
                  )}
                  
                  {safeEmbed && (
                    <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden bg-slate-50 p-2">
                      <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Preview</p>
                      <div dangerouslySetInnerHTML={{ __html: safeEmbed }} />
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-800">Quiz Questions & Feedback</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{isEditing ? 'Edit existing questions, options, or feedback messages.' : 'Optional. Create questions and provide specific feedback for each option.'}</p>
                  </div>
                  <button onClick={handleAddQuestion} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                    <SafeIcon icon={FiPlus} /> Add Question
                  </button>
                </div>

                {questions.length === 0 ? (
                  <div className="text-center py-10 bg-slate-50 border border-dashed border-slate-200 rounded-xl">
                    <p className="text-sm text-slate-500 font-medium">No quiz questions added.</p>
                    <p className="text-xs text-slate-400 mt-1">You can skip this step if not needed.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {questions.map((q, qIndex) => (
                      <div key={qIndex} className="p-5 border border-slate-200 rounded-xl bg-slate-50 relative">
                        <button onClick={() => handleRemoveQuestion(qIndex)} className="absolute top-4 right-4 text-slate-400 hover:text-red-500 transition-colors">
                          <SafeIcon icon={FiTrash2} />
                        </button>
                        <Field label={`Question ${qIndex + 1}`}>
                          <input value={q.question} onChange={e => updQuestion(qIndex, { question: e.target.value })} placeholder="Enter question..." className={inputCls} />
                        </Field>
                        <div className="mt-4 grid grid-cols-1 gap-4">
                          {q.options.map((opt, oIndex) => (
                            <div key={oIndex} className="flex flex-col gap-2 p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                              <div className="flex items-center gap-3">
                                <input type="radio" name={`correct-${qIndex}`} checked={q.answer === opt && opt !== ''} onChange={() => updQuestion(qIndex, { answer: opt })} className="accent-blue-600 mt-0.5 w-4 h-4" title="Mark as correct answer" />
                                <input value={opt} onChange={e => updOption(qIndex, oIndex, e.target.value)} placeholder={`Option ${String.fromCharCode(65 + oIndex)} text...`} className="w-full px-3 py-2 border border-slate-200 rounded text-sm focus:outline-none focus:border-blue-500 bg-slate-50" />
                              </div>
                              <div className="pl-7">
                                <textarea 
                                  value={q.explanations?.[oIndex] || ''} 
                                  onChange={e => updExplanation(qIndex, oIndex, e.target.value)} 
                                  placeholder={`Feedback message if option ${String.fromCharCode(65 + oIndex)} is selected (e.g. "Incorrect. Antivirus software...")`} 
                                  className="w-full px-3 py-2 border border-slate-200 rounded text-xs focus:outline-none focus:border-blue-500 bg-slate-50 min-h-[60px] resize-y" 
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-400 mt-3">Select the radio button next to the correct option.</p>
                      </div>
                    ))}
                    <button onClick={handleAddQuestion} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                      <SafeIcon icon={FiPlus} /> Add Question
                    </button>
                  </div>
                  
                )}
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">Assign to Campaigns</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Optional. Assign this module directly to existing campaigns.</p>
                </div>

                {loadingCampaigns ? (
                  <div className="flex justify-center py-10"><SafeIcon icon={FiLoader} className="animate-spin text-2xl text-slate-400" /></div>
                ) : campaigns.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">No campaigns found.</p>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
                    {campaigns.filter(c => c.status !== 'Completed').map(c => {
                      const isSelected = selectedCampaigns.includes(c.id);
                      return (
                        <label key={c.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'}`}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggleCampaign(c.id)} className="accent-blue-600 rounded" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800">{c.campaignName}</p>
                            <p className="text-xs text-slate-500 mt-0.5">{c.status} • {c.audience}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            )}
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
            {step === 1 ? (
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                Cancel
              </button>
            ) : (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                <SafeIcon icon={FiChevronLeft} /> Back
              </button>
            )}

            {step < 3 ? (
              <button onClick={() => setStep(step + 1)} disabled={step === 1 && !canProceedStep1}
                className={`flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${step === 1 && !canProceedStep1 ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                Next <SafeIcon icon={FiChevronRight} />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading}
                className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg transition-colors ${loading ? 'bg-blue-400 cursor-not-allowed text-white' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md'}`}>
                {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiCheck} />}
                {isEditing ? 'Save Changes' : 'Create Module'}
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default CreateModuleModal;